// Interceptor de mock — solo activo si environment.useMocks === true
// Intercepta URLs de la API y devuelve datos mock con delay simulado
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, delay } from 'rxjs';
import { API_ENDPOINTS } from '../app/core/infrastructure/config/api.config';
import { MOCK_VEHICLE_BRANDS, MOCK_CITIES, MOCK_VEHICLE_YEARS } from './mock-data/vehicles.mock';
import { MOCK_PLANS } from './mock-data/plans.mock';
import { MOCK_QUOTE_RESPONSE } from './mock-data/quote-response.mock';
import { MOCK_CIRCULATION_ZONES, MOCK_COUNTRIES } from './mock-data/catalogs.mock';
import { MOCK_ADVISORS } from './mock-data/advisors.mock';
import { MOCK_PENDING_INVOICES } from './mock-data/pending-invoices.mock';
import { ApiResponse } from '../app/core/domain/interfaces/api-response.model';

// Delay aleatorio entre 500ms y 1500ms para simular latencia real
const randomDelay = (): number => Math.floor(Math.random() * 1000) + 500;

// Crea una respuesta mock exitosa
function mockResponse<T>(data: T): HttpResponse<ApiResponse<T>> {
  return new HttpResponse({
    status: 200,
    body: {
      data,
      message: 'OK',
      statusCode: 200,
      success: true,
    },
  });
}

// Mock del token OAuth2: forma cruda (no envuelta en ApiResponse), igual que el backend real
function mockTokenResponse(): HttpResponse<unknown> {
  return new HttpResponse({
    status: 200,
    body: {
      token_type: 'Bearer',
      expires_in: 3599,
      ext_expires_in: 3599,
      access_token: 'mock-access-token-' + Date.now(),
    },
  });
}

// Mapa de URLs mock → datos de respuesta
function getMockResponse(url: string): HttpResponse<unknown> | null {
  // Auth token (OAuth2 client_credentials)
  if (url.includes(API_ENDPOINTS.auth.token)) {
    return mockTokenResponse();
  }

  // Marcas de vehículos
  if (url.includes('/vehicles/brands')) {
    return mockResponse(MOCK_VEHICLE_BRANDS);
  }

  // Años de vehículos
  if (url.includes('/vehicles/years')) {
    return mockResponse(MOCK_VEHICLE_YEARS);
  }

  // Búsqueda de vehículo por placa
  if (url.includes('/vehicles/lookup/')) {
    const plate = url.split('/vehicles/lookup/')[1];
    return mockResponse({
      brand: 'Toyota',
      model: 'Corolla',
      year: 2022,
      found: plate?.length >= 6,
    });
  }

  // Ciudades
  if (url.includes(API_ENDPOINTS.cities.list)) {
    return mockResponse(MOCK_CITIES);
  }

  // Catálogo: países
  if (url.includes(API_ENDPOINTS.catalogs.countries)) {
    return mockResponse({ countries: MOCK_COUNTRIES });
  }

  // Catálogo: zonas de circulación (catálogo de "ciudad" en UI)
  if (url.includes(API_ENDPOINTS.catalogs.circulationZones)) {
    return mockResponse({ circulation_zones: MOCK_CIRCULATION_ZONES });
  }

  // Asesores SURA 
  if (url.includes(API_ENDPOINTS.advisors.list)) {
    const suffix = url.split(API_ENDPOINTS.advisors.list)[1] ?? '';
    const code = suffix.replace('/', '').split('?')[0];
    if (!code) return mockResponse({ Asesores: MOCK_ADVISORS });

    const found = MOCK_ADVISORS.filter((advisor) => advisor.Cod === code);
    return found.length > 0 ? mockResponse({ Asesor: found }) : mockResponse('');
  }

  // Pago (PlacetoPay via membresia)
  if (url.includes(API_ENDPOINTS.payments.start)) {
    return mockResponse({
      status: { status: 'APPROVED', reason: '00', message: 'Pago aprobado (mock)', date: new Date().toISOString() },
      requestId: 'mock-request-id',
      processUrl: null,
    });
  }

  if (url.includes('/clientes/consultaFactura/')) {
    return new HttpResponse({
      status: 200,
      body: {
        ...MOCK_PENDING_INVOICES,
        userInfo: 'Integracion Suratech',
        timestamp: 'Execution time: 124 ms',
      },
    });
  }

  if (url.includes(API_ENDPOINTS.payments.token)) {
    return mockResponse({
      status: 'APPROVED',
      reason: '00',
      message: 'Pago aprobado (mock)',
      date: new Date().toISOString(),
    });
  }

  // Planes
  if (url.includes(API_ENDPOINTS.quoter.plans)) {
    return mockResponse(MOCK_PLANS);
  }

  // Cotización
  if (url.includes(API_ENDPOINTS.quoter.calculate)) {
    return mockResponse(MOCK_QUOTE_RESPONSE);
  }

  return null;
}

export const mockApiInterceptor: HttpInterceptorFn = (req, next) => {
  const mockResp = getMockResponse(req.url);

  if (mockResp) {
    return of(mockResp).pipe(delay(randomDelay()));
  }

  // Si no hay mock para esta URL, pasar al siguiente interceptor
  return next(req);
};
